import React, {
  ChangeEventHandler,
  useEffect,
  useState,
  useRef,
  useCallback,
} from 'react';
import { RouteLog } from '../common/ObjectTypes';
import LogsFilter from '../activityLogs/ActivityLogsFilter';
import {
  ArrowDown,
  ArrowRight,
  CopyToClipboard,
  SortedDown,
  SortedUp,
} from '../common/ProjectIcons';
import { copyToClipboard } from '../common/copyToClipboard';
import { toast } from 'react-toastify';
import Spinner from '../common/Spinner';
import { HoverTooltip } from '../common/HoverTooltip';
import { useTimezone } from '../../context/TimezoneContext';
import { getUtcOffsets } from '../common/getUtcOffsets';
import { useTranslation } from 'react-i18next';
import NoResults from '../common/NoResults';
import { tableClasses, skeletonClasses } from '../common/tailwindClassnames';
import {
  getLogLevelIcon,
  getLogLevelLabel,
} from '../activityLogs/logLevelIcons';
import { RelativeTimeTooltip } from '../activityLogs/RelativeTimeTooltip';

type RouteLogProps = {
  logs: RouteLog[];
  reloadLogs: boolean;
  isAppendingLogs?: boolean;
  nextCursor: string | null;
  selectedTimezone: any;
  onSelectTimezone: Function;
  onNextPageClicked: Function;
  requestSort: Function;
  pageLoaded: boolean;
  filters: any[];
  globalSearchStr: string;
  onChangeLogGlobalSearch: (value: string) => void;
  onChangeLogStatus: ChangeEventHandler;
  onChangeLogType: ChangeEventHandler;
  onChangeLogDate: Function;
  onResetLogFilters: Function;
  onSelectLog: Function;
  onRefreshLogs: Function;
  logsError?: boolean;
  // Pass-through to LogsFilter: pre-filter to Warning minimum on unhealthy Routes.
  defaultMinimumWarning?: boolean;
  hasActiveLogLevel?: boolean;
  activeLogLevel?: { name: string; id: string } | null;
  activeLogType?: string | null;
};

const isComplexValue = (value: any) =>
  value !== null && typeof value === 'object';

// Render a stored offset (e.g. "-06:00", "+05:30", "+00:00") as a compact label
// for the logs header: "UTC-6", "UTC+5:30", "UTC".
export const formatShortUtcOffset = (value: string): string => {
  if (!value) return 'UTC';
  const sign = value.trim().startsWith('-') ? '-' : '+';
  const [h, m] = value.replace(/^[+-]/, '').split(':');
  const hours = parseInt(h, 10) || 0;
  const minutes = parseInt(m, 10) || 0;
  if (hours === 0 && minutes === 0) return 'UTC';
  const minutesPart = minutes ? `:${String(minutes).padStart(2, '0')}` : '';
  return `UTC${sign}${hours}${minutesPart}`;
};

const RouteDetailsLog = ({
  logs,
  reloadLogs,
  isAppendingLogs = false,
  nextCursor,
  selectedTimezone,
  onSelectTimezone,
  onNextPageClicked,
  requestSort,
  pageLoaded,
  filters,
  globalSearchStr,
  onChangeLogGlobalSearch,
  onChangeLogStatus,
  onChangeLogType,
  onChangeLogDate,
  onResetLogFilters,
  onSelectLog,
  onRefreshLogs,
  logsError = false,
  defaultMinimumWarning = false,
  hasActiveLogLevel = false,
  activeLogLevel = null,
  activeLogType = null,
}: RouteLogProps) => {
  const { t } = useTranslation();
  const { timezone } = useTimezone();
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [timezones, setTimezones] = useState<any[]>([]);
  const [sortDirection, setSortDirection] = useState<boolean>(true);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [logDetails, setLogDetails] = useState<RouteLog | null>(null);
  const [collapsedKeys, setCollapsedKeys] = useState<Set<string>>(new Set());
  // Details are fetched per row into one shared slot, so a slower earlier
  // request must not overwrite a newer one. Only the latest request wins.
  const detailsRequest = useRef(0);

  // The logs list flows in the normal page, so we watch the window scroll
  // position and load the next page as it nears the bottom of the document.
  // Memoized on its inputs (including nextCursor) so it always reads the
  // current cursor; the listener effect below re-binds when it changes.
  const handleScroll = useCallback(() => {
    // Bail while a page is loading or appending so a scroll event in the gap
    // before the request settles can't re-trigger the same fetch.
    if (reloadLogs || isAppendingLogs) return;

    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const clientHeight = window.innerHeight;
    const scrollHeight = document.documentElement.scrollHeight;

    // Only trigger next page load when the page is actually scrollable (so the
    // programmatic open-scroll on a short page can't fire this), we're very
    // close to the bottom (within 20px), and there's more content to load.
    if (
      scrollHeight > clientHeight &&
      scrollTop + clientHeight >= scrollHeight - 20 &&
      nextCursor
    ) {
      onNextPageClicked(); // Load next page
    }
  }, [reloadLogs, isAppendingLogs, onNextPageClicked, nextCursor]);

  // Debounced handler ref
  const scrollTimeoutRef = useRef<number | null>(null);
  const debouncedScrollHandler = useCallback(() => {
    if (scrollTimeoutRef.current) {
      window.clearTimeout(scrollTimeoutRef.current);
    }
    scrollTimeoutRef.current = window.setTimeout(() => {
      handleScroll();
    }, 50);
  }, [handleScroll]);
  const formatDatetime = (datetime: string) => {
    const date = new Date(datetime);

    // Parse timezone offset
    const sign = timezone.value.startsWith('-') ? -1 : 1;
    const hours = parseInt(timezone.value.slice(1, 3), 10);
    const minutes = parseInt(timezone.value.slice(3, 5), 10) || 0;

    const offsetMilliseconds = sign * (hours * 60 + minutes) * 60 * 1000;

    const adjustedDate = new Date(date.getTime() + offsetMilliseconds);

    const year = adjustedDate.getUTCFullYear();
    const month = String(adjustedDate.getUTCMonth() + 1).padStart(2, '0');
    const day = String(adjustedDate.getUTCDate()).padStart(2, '0');
    const hoursFormatted = String(adjustedDate.getUTCHours()).padStart(2, '0');
    const minutesFormatted = String(adjustedDate.getUTCMinutes()).padStart(
      2,
      '0',
    );
    const secondsFormatted = String(adjustedDate.getUTCSeconds()).padStart(
      2,
      '0',
    );

    return `${year}-${month}-${day} ${hoursFormatted}:${minutesFormatted}:${secondsFormatted}`;
  };
  const toggleDetails = async (id: string) => {
    if (expandedRow === id) {
      setExpandedRow(null);
      // Collapsing ends the row's business: stop waiting on its response and
      // let go of what it fetched, so nothing can settle into the slot later.
      detailsRequest.current += 1;
      setLoadingDetails(false);
      setLogDetails(null);
    } else {
      setExpandedRow(id);
      setCollapsedKeys(new Set());
      // Drop the previous row's details rather than leaving them on screen
      // (and copyable) under the row now expanding.
      setLogDetails(null);
      setLoadingDetails(true);
      const request = ++detailsRequest.current;
      // `getLogDetails` routes failures through `handleError`, which rethrows
      // everything that isn't a 401 and returns undefined for one — so this
      // both throws and resolves empty in production. Untrapped, the throw
      // skipped the line below and pinned the row to a spinner for good.
      let temp: RouteLog | undefined;
      let failed = false;
      try {
        temp = await onSelectLog(id);
        failed = !temp;
      } catch (err) {
        console.error('Failed to load log details', err);
        failed = true;
      }
      // Expand A, then B, and A's slower response would otherwise land in
      // the slot while B is the open row — showing and copying A's JSON
      // under B. Whoever is no longer the latest request drops its result.
      if (request !== detailsRequest.current) return;
      setLoadingDetails(false);
      if (failed) {
        // Nothing to show, so don't leave an empty panel hanging open —
        // close the row and say what happened in a toast.
        setExpandedRow(null);
        setLogDetails(null);
        toast.error(
          t(
            'There was an error loading the log details. Please contact support.',
          ),
          { position: toast.POSITION.TOP_RIGHT },
        );
        return;
      }
      setLogDetails(temp ?? null);
    }
  };

  useEffect(() => {
    let temp = getUtcOffsets();
    setTimezones(temp);
  }, []);

  // A filter, search or sort can drop the open row from the list. Nothing reset
  // `expandedRow`, so the id lingered and the row sprang open again — with the
  // previous fetch's details and no request in flight — the moment that entry
  // came back. Keyed on the row's absence, not on the array's identity, so
  // appending a page during infinite scroll doesn't collapse what's open.
  useEffect(() => {
    if (
      expandedRow &&
      !logs.some((entry: RouteLog) => entry.id === expandedRow)
    ) {
      setExpandedRow(null);
      setLoadingDetails(false);
      setLogDetails(null);
    }
  }, [logs, expandedRow]);

  // When the logs finish loading, scroll the page down once so the Route
  // header/tabs move up and out of the way, pinning the filter just below the
  // fixed nav. We measure the filter's live position rather than using a fixed
  // offset so long Route names (which wrap the header onto extra lines)
  // still scroll the right amount. The nav height is read at runtime so it stays
  // correct whether or not the environment banner is present.
  const filterRef = useRef<HTMLDivElement | null>(null);
  const didAutoScroll = useRef(false);
  useEffect(() => {
    if (
      didAutoScroll.current ||
      !pageLoaded ||
      reloadLogs ||
      logs.length === 0 ||
      !filterRef.current
    )
      return;
    didAutoScroll.current = true;
    // Two nested rAFs: the first fires before the browser paints the loaded
    // table (layout still stale), the second after paint so
    // getBoundingClientRect() reflects the real layout.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!filterRef.current) return;
        const nav = document.querySelector("[data-testid='main-nav-wrapper']");
        const navHeight = nav ? nav.getBoundingClientRect().height : 0;
        // The tab strip is sticky, so it parks itself directly under
        // the top strip and would cover the filter if we only cleared
        // the nav.
        const tabs = document.querySelector(
          "[data-testid='RouteDetails-tabs']",
        );
        const tabsHeight = tabs ? tabs.getBoundingClientRect().height : 0;
        const top =
          filterRef.current.getBoundingClientRect().top +
          window.scrollY -
          navHeight -
          tabsHeight;
        window.scrollTo({ top, behavior: 'smooth' });
      });
    });
  }, [pageLoaded, reloadLogs, logs.length]);

  // A full (non-append) reload — filter, sort, refresh — can shrink the result
  // set. Since the list flows in the page, a stale scroll offset would otherwise
  // leave the filter/rows mispositioned. Re-arm the one-shot pin so it re-runs
  // once the new results render, bringing the filter back under the nav.
  // Appends (isAppendingLogs) keep the user's place and must not re-pin.
  useEffect(() => {
    if (reloadLogs && !isAppendingLogs) {
      didAutoScroll.current = false;
    }
  }, [reloadLogs, isAppendingLogs]);

  useEffect(() => {
    window.addEventListener('scroll', debouncedScrollHandler, {
      passive: true,
    });
    return () => {
      window.removeEventListener('scroll', debouncedScrollHandler);
      if (scrollTimeoutRef.current) {
        window.clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [debouncedScrollHandler]);

  const formatLogDetails = (logDetails: RouteLog | null, logLevel: number) => {
    if (!logDetails) return null;

    const isError = logLevel === 40;

    const isLongString = (value: any) =>
      typeof value === 'string' && (value.includes('\n') || value.length > 80);

    const formatValue = (key: string, value: any): React.ReactNode => {
      if (value === null || value === undefined)
        return <span className="text-unlisted-neutral-gray-400">null</span>;
      if (typeof value === 'boolean') return <span>{String(value)}</span>;
      if (typeof value === 'string' || typeof value === 'number') {
        if (isLongString(value)) {
          const collapsed = collapsedKeys.has(key);
          if (collapsed)
            return <span className="text-unlisted-neutral-gray-400">"…"</span>;
          return (
            <pre className="overflow-x-auto whitespace-pre-wrap font-[inherit] text-sm">
              {value}
            </pre>
          );
        }
        return (
          <span>
            {typeof value === 'string' ? `"${value}"` : String(value)}
          </span>
        );
      }
      const collapsed = collapsedKeys.has(key);
      if (collapsed) {
        return (
          <span className="text-unlisted-neutral-gray-400">
            {Array.isArray(value) ? '[…]' : '{…}'}
          </span>
        );
      }
      return (
        <pre className="inline-block overflow-x-auto whitespace-pre-wrap align-top font-[inherit] text-sm">
          {JSON.stringify(value, null, 2)}
        </pre>
      );
    };

    const renderRows = (entries: [string, any][], section: string) =>
      entries.map(([key, value]) => {
        const scopedKey = `${section}:${key}`;
        const isErrorField =
          isError &&
          (key === 'error' ||
            key.includes('error') ||
            key.includes('server_response_body') ||
            key.includes('server_response_status') ||
            (typeof value === 'string' &&
              value.toLowerCase().includes('error')));
        const hasToggle = isComplexValue(value) || isLongString(value);
        const collapsed = collapsedKeys.has(scopedKey);
        const toggle = () =>
          setCollapsedKeys((prev) => {
            const next = new Set(prev);
            collapsed ? next.delete(scopedKey) : next.add(scopedKey);
            return next;
          });

        return (
          <div
            key={key}
            className={`mb-1 flex items-start gap-1.5 ${isErrorField ? 'text-ui-status-error-destructive' : ''} transition-colors duration-300`}
          >
            {hasToggle ? (
              <button
                type="button"
                onClick={toggle}
                className="mt-px shrink-0 cursor-pointer leading-none text-ui-text-secondary-text transition-colors duration-150 hover:text-ui-text-primary-text"
                aria-label={collapsed ? t('Expand') : t('Collapse')}
              >
                {collapsed ? '▶' : '▼'}
              </button>
            ) : (
              <span className="w-[0.75rem] shrink-0" />
            )}
            <span className="shrink-0 whitespace-nowrap text-ui-text-primary-text">
              {key}:
            </span>
            <span
              className={`min-w-0 flex-1 ${isErrorField ? '' : 'text-teal-1'}`}
            >
              {formatValue(scopedKey, value)}
            </span>
          </div>
        );
      });

    const topLevel = Object.entries(logDetails).filter(
      ([k]) => k !== 'details',
    );
    const details = logDetails.details
      ? Object.entries(logDetails.details)
      : [];

    return (
      <div className="w-full text-sm text-ui-text-primary-text">
        {renderRows(topLevel, 'top')}
        {details.length > 0 && (
          <>
            <div className="mb-1">
              <span className="inline-block w-40 text-ui-text-primary-text">
                details:
              </span>
            </div>
            <div className="pl-2">{renderRows(details, 'details')}</div>
          </>
        )}
      </div>
    );
  };

  return (
    <div>
      <div ref={filterRef} className="mt-4 flex w-full flex-col">
        <LogsFilter
          globalSearchStr={globalSearchStr}
          timezones={timezones}
          selectedTimezone={selectedTimezone}
          onChangeGlobalSearch={onChangeLogGlobalSearch}
          onResetFilters={onResetLogFilters}
          onChangeStatusFilter={onChangeLogStatus}
          onChangeLogType={onChangeLogType}
          onChangeLogDate={onChangeLogDate}
          onSelectTimezone={onSelectTimezone}
          onRefresh={onRefreshLogs}
          reloadLogs={reloadLogs}
          defaultMinimumWarning={defaultMinimumWarning}
          hasActiveLogLevel={hasActiveLogLevel}
          activeLogLevel={activeLogLevel}
          activeLogType={activeLogType}
        />
        <div className="mb-4 h-auto w-full">
          <div
            className={`flex h-fit w-full flex-col ${tableClasses.container}`}
          >
            <table className="w-full table-fixed cursor-default">
              <thead>
                <tr className={tableClasses.header}>
                  <th className={`${tableClasses.headerCell} w-20 pl-4`}>
                    {t('Level')}
                  </th>
                  <th className={`w-40 px-1.5 py-3`}>
                    <span
                      onClick={() => {
                        requestSort(sortDirection);
                        setSortDirection(!sortDirection);
                      }}
                      className={tableClasses.sortIcon}
                    >
                      {t('Time')} ({formatShortUtcOffset(timezone?.value)}){' '}
                      {sortDirection ? <SortedUp /> : <SortedDown />}
                    </span>
                  </th>
                  <th className={`py-3 pl-1 pr-1.5`}>{t('Title')}</th>
                </tr>
              </thead>
              {reloadLogs && !isAppendingLogs && (
                <tbody className="w-full text-sm">
                  {Array.from({ length: 12 }).map((_, i) => (
                    <tr key={i} className={tableClasses.row}>
                      <td className={`${tableClasses.cell} w-20 pl-4`}>
                        <div className="flex items-center gap-2">
                          <div className={`h-3 w-3 ${skeletonClasses.base}`} />
                          <div
                            className={`h-4 w-4 ${skeletonClasses.base} rounded-full`}
                          />
                        </div>
                      </td>
                      <td className={`w-40 px-1.5 py-3`}>
                        <div className={`h-4 w-32 ${skeletonClasses.base}`} />
                      </td>
                      <td className={`py-3 pl-1 pr-1.5`}>
                        <div
                          className={`h-4 ${i % 3 === 0 ? 'w-3/4' : i % 3 === 1 ? 'w-1/2' : 'w-2/3'} ${skeletonClasses.base}`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              )}
              {logs.length === 0 && !reloadLogs && (
                <tbody className="w-full text-sm">
                  <tr>
                    <td colSpan={3} className="py-8 text-center">
                      {logsError ? (
                        <NoResults
                          headerMsg={t('Failed to Load Logs' as any)}
                          labelMsg={t(
                            'Unable to load activity logs. Please try again later.' as any,
                          )}
                        />
                      ) : (
                        <NoResults
                          headerMsg={t('No Results Found')}
                          labelMsg={t(
                            'You may need to adjust your search or filters to find what you are looking for',
                          )}
                        />
                      )}
                    </td>
                  </tr>
                </tbody>
              )}
              {logs.length >= 1 && (!reloadLogs || isAppendingLogs) && (
                <tbody className="w-full text-sm">
                  {logs.map((log: RouteLog) => (
                    <React.Fragment key={log.id}>
                      <tr
                        className={`${tableClasses.row} cursor-pointer border-t`}
                        onClick={() => toggleDetails(log.id)}
                      >
                        <td
                          className={`${tableClasses.cell} w-20 pl-4 align-middle`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="flex cursor-pointer items-center font-semibold leading-none text-ui-cta-primary transition-colors duration-300">
                              {expandedRow === log.id ? (
                                <ArrowDown />
                              ) : (
                                <ArrowRight />
                              )}
                            </span>
                            <HoverTooltip
                              content={t(
                                getLogLevelLabel(log.log_level) as any,
                              )}
                              ariaLabel={t(
                                getLogLevelLabel(log.log_level) as any,
                              )}
                              role="img"
                              className="cursor-default items-center"
                            >
                              {getLogLevelIcon(log.log_level)}
                            </HoverTooltip>
                          </div>
                        </td>
                        <td
                          className={`px-1.5 py-3 text-left align-middle text-ui-text-primary-text transition-colors duration-300`}
                        >
                          <RelativeTimeTooltip isoDatetime={log.created_at}>
                            {formatDatetime(log.created_at)}
                          </RelativeTimeTooltip>
                        </td>
                        <td
                          className={`max-w-0 truncate py-3 pl-1 pr-8 text-left align-middle`}
                        >
                          {log.title}
                        </td>
                      </tr>
                      {expandedRow === log.id && (
                        <tr>
                          <td
                            colSpan={3}
                            className={`${tableClasses.cell} text-wrap bg-white pb-8 pl-8 pr-8 text-xs text-ui-text-primary-text`}
                          >
                            {loadingDetails ? (
                              <Spinner />
                            ) : (
                              <div className="relative">
                                {logDetails && (
                                  <button
                                    type="button"
                                    data-testid="RouteDetailsLog-copy-button"
                                    onClick={(e) => {
                                      // The parent row's onClick collapses the row.
                                      e.stopPropagation();
                                      copyToClipboard(
                                        JSON.stringify(logDetails, null, 2),
                                        t,
                                      );
                                    }}
                                    className="absolute right-0 top-0 flex-shrink-0 opacity-50 transition-opacity hover:opacity-100"
                                    aria-label={t('Copy to clipboard')}
                                  >
                                    <CopyToClipboard />
                                  </button>
                                )}
                                {formatLogDetails(logDetails, log.log_level)}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                  {isAppendingLogs && (
                    <tr>
                      <td
                        colSpan={3}
                        className="py-3 pl-4 text-left text-ui-text-primary-text transition-colors duration-300"
                      >
                        {t('Loading...')}
                      </td>
                    </tr>
                  )}
                </tbody>
              )}
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RouteDetailsLog;
